import { formatTargetProducingRate } from "../configurationPageUtils";

export type SkuTableDataType = {
  skuCode: string;
  skuName: string;
  description: string;
  stationName?: string;
  scale: string;
  teamSizes: number[];
  rate: string;
  isLoading?: boolean;
  isPartialData?: boolean;
  errorMessage?: string;
  targetRate?: number;
  timeWindow?: number;
  isTimePerUnit?: boolean;
  isLinearScaling?: boolean; // Whether team sizes scale linearly with target rates
  teamSizeMin?: number | null; // Minimum team size for this SKU
  teamSizeMax?: number | null; // Maximum team size for this SKU
};

export const prepareSkuTableData = (
  playbookData: any,
  allSkusData: any[],
  skuStationData: Map<string, any[]>
): SkuTableDataType[] => {
  // Group plays by SKU code only (not by rate) to enable linear scaling detection
  // Structure to hold SKU data with multiple rates and team sizes
  interface SkuData {
    skuCode: string;
    skuName: string;
    description: string;
    scale: string;
    rates: { rate: string; teamSize: number }[];
    isLoading: boolean;
    targetRate?: number;
    timeWindow?: number;
    isTimePerUnit?: boolean;
    isPartialData?: boolean;
    errorMessage?: string;
  }

  // Map to store SKU data keyed only by SKU code
  const groupedSkus = new Map<string, SkuData>();

  // Process plays from the newer format (senseConfig)
  if (
    playbookData &&
    playbookData.senseConfig?.process?.playbook &&
    Array.isArray(playbookData.senseConfig.process.playbook)
  ) {
    // Loop through each playbook
    playbookData.senseConfig.process.playbook.forEach((playbook: any) => {
      const skuCode = playbook.skuCode || "N/A";

      // Initialize SKU data if not already done
      if (!groupedSkus.has(skuCode)) {
        // Find matching SKU from allSkusData
        const matchingSku = allSkusData.find(
          sku => sku.skuId === skuCode || sku.name === skuCode
        );

        // Get SKU station data if available
        const skuStation = skuStationData.get(skuCode)?.[0];

        groupedSkus.set(skuCode, {
          skuCode,
          skuName: matchingSku ? matchingSku.name : skuStation?.name || "N/A",
          description: matchingSku?.type || playbook.description || "N/A",
          scale: skuStation?.skuScale || playbook.scale || "N/A",
          rates: [],
          isLoading: false,
          targetRate: skuStation?.targetRate,
          timeWindow: skuStation?.timeWindow,
          isTimePerUnit: skuStation?.isTimePerUnit,
          isPartialData: skuStation?.isPartialData,
          errorMessage: skuStation?.errorMessage,
        });
      }

      // Add play data with rates and team sizes
      if (playbook.plays && Array.isArray(playbook.plays)) {
        playbook.plays.forEach((play: any) => {
          if (
            play.teamSize !== undefined &&
            play.targetProducingRate !== undefined
          ) {
            const rate = formatTargetProducingRate(play.targetProducingRate);
            groupedSkus.get(skuCode)?.rates.push({
              rate,
              teamSize: play.teamSize,
            });
          }
        });
      }
    });
  }

  // Process plays from the old format if needed
  if (
    groupedSkus.size === 0 &&
    playbookData?.playbook &&
    playbookData.playbook.plays &&
    Array.isArray(playbookData.playbook.plays)
  ) {
    const skuCode = playbookData.playbook.skuCode || "N/A";

    // Initialize SKU data
    if (!groupedSkus.has(skuCode)) {
      // Find matching SKU
      const matchingSku = allSkusData.find(
        sku => sku.skuId === skuCode || sku.name === skuCode
      );

      // Get SKU station data
      const skuStation = skuStationData.get(skuCode)?.[0];

      groupedSkus.set(skuCode, {
        skuCode,
        skuName: matchingSku ? matchingSku.name : skuStation?.name || "N/A",
        description:
          matchingSku?.type || playbookData.playbook.description || "N/A",
        scale: skuStation?.skuScale || playbookData.playbook.scale || "N/A",
        rates: [],
        isLoading: false,
        targetRate: skuStation?.targetRate,
        timeWindow: skuStation?.timeWindow,
        isTimePerUnit: skuStation?.isTimePerUnit,
        isPartialData: skuStation?.isPartialData,
        errorMessage: skuStation?.errorMessage,
      });
    }

    // Add play data
    playbookData.playbook.plays.forEach((play: any) => {
      if (
        play.teamSize !== undefined &&
        play.targetProducingRate !== undefined
      ) {
        const rate = formatTargetProducingRate(play.targetProducingRate);
        groupedSkus.get(skuCode)?.rates.push({
          rate,
          teamSize: play.teamSize,
        });
      }
    });
  }

  // Process the grouped data to detect linear scaling
  const result: SkuTableDataType[] = [];

  groupedSkus.forEach((skuData, skuCode) => {
    // Extract team sizes
    const teamSizes = skuData.rates.map(r => r.teamSize).sort((a, b) => a - b);

    // Create a map of team size to rate value for linear scaling detection
    const teamSizeToRate: Record<number, number> = {};
    skuData.rates.forEach(rateData => {
      // Convert string rate like "450/hour" to number for calculation
      const numericRate = parseFloat(rateData.rate.split("/")[0]);
      if (!isNaN(numericRate)) {
        teamSizeToRate[rateData.teamSize] = numericRate;
      }
    });

    // Detect linear scaling
    let isLinearScaling = false;
    if (teamSizes.length > 1) {
      // For linear scaling, ratios of team size to target rate should be consistent
      const firstSize = teamSizes[0];
      const firstRate = teamSizeToRate[firstSize];

      if (firstRate !== undefined && firstSize > 0) {
        const expectedRatio = firstRate / firstSize;

        // Check if all team sizes follow the same ratio
        isLinearScaling = teamSizes.every(size => {
          const rate = teamSizeToRate[size];
          return (
            rate !== undefined && Math.abs(rate / size - expectedRatio) < 0.001
          );
        });
      }
    }

    // Get min/max team sizes
    const teamSizeMin = teamSizes.length > 0 ? teamSizes[0] : null;
    const teamSizeMax =
      teamSizes.length > 0 ? teamSizes[teamSizes.length - 1] : null;

    // Find the first rate (for display when grouped)
    const firstRateData = skuData.rates[0] || { rate: "" };

    // Create the SKU table entry
    result.push({
      skuCode: skuData.skuCode,
      skuName: skuData.skuName,
      description: skuData.description,
      scale: skuData.scale,
      rate: firstRateData.rate,
      teamSizes,
      isLoading: skuData.isLoading,
      targetRate: skuData.targetRate,
      timeWindow: skuData.timeWindow,
      isTimePerUnit: skuData.isTimePerUnit,
      isPartialData: skuData.isPartialData,
      errorMessage: skuData.errorMessage,
      isLinearScaling,
      teamSizeMin,
      teamSizeMax,
    });
  });

  return result;
};
